import { BaseGameController } from '@/components/game/implementations/_base/_domain/controller/game-controller';
import { injectable, inject } from 'inversify';
import { IGameRepresentation, INextGameInfo, IGame, GameIdentity, IGameConfiguration, GameState, GameCallboard } from '@/components/game/_domain/models';
import { NextGameData, CatchUpData, ResetData, IBingo75AuthenticationResponse } from '@/components/game/_domain';
import { IServerServiceFactory } from '@/app-modules/server-service';
import { IWinnersTrackerService } from '@/components/game/_domain/modules/winners-tracker-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IConfigurationService } from '@/app-modules/config';
import { IErrorService } from '@/app-modules/error';


interface IBingo75NextGameInfo extends INextGameInfo {
  patternName?: string;
}

interface IBingo75GameRepresentation extends IGameRepresentation {
    nextGame?: IBingo75NextGameInfo;
}

class Bingo75Game implements IGame {
  public constructor(public readonly gameIdentity: GameIdentity, public readonly gameConfiguration: IGameConfiguration) {
    this.connectionDebugInfo = 'Disconnected';
    this.state = GameState.InPlay;
  }

  public connectionDebugInfo: string;
  public state: GameState;
  public representation?: IBingo75GameRepresentation;

  public updateRepresentation(newGameRepresentation: Partial<IBingo75GameRepresentation>): void {
    this.representation = Object.assign({}, this.representation, newGameRepresentation || {});
  }
}

class Patterns {
  public currentPattern: string = '';
  public currentPatternName: string = '';
  public nextPattern: string = '';
  public nextPatternName: string = '';
}

@injectable()
class Bingo75GameController extends BaseGameController {
  protected game: Bingo75Game;
  public patterns: Patterns = new Patterns();


  public constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IServerServiceFactory) serverServiceFactory: IServerServiceFactory,
    @inject(SERVICE_IDENTIFIERS.IWinnersTrackerService) winnersTrackerService: IWinnersTrackerService,
    @inject(SERVICE_IDENTIFIERS.IErrorService) errorService: IErrorService
  ) {
    super(configurationService, serverServiceFactory, winnersTrackerService, errorService);
  }

  public initialise(gameIdentity: GameIdentity) {
    this.game = this.getInitialGame(gameIdentity);
    this.initialiseGameServerConnection();
    this.initialiseGameRetryPolicySettings();
    this.initialiseGameServerService();
  }

  protected subscribeToGameEvents(): void {
    this.serverSubscriptionStore.push(
      this.gameServerService.subscribeCatchUp(this.handleCatchUpMessage.bind(this)),
      this.gameServerService.subscribeWinners(this.handleWinnersMessage.bind(this)),
      this.gameServerService.subscribeReset(this.handleResetMessage.bind(this)),
      this.gameServerService.subscribeCalls(this.handleCallsMessage.bind(this)),
      this.gameServerService.subscribeNextGame(this.handleNextGameMessage.bind(this)),
      this.gameServerService.subscribePrizeWinner(this.handlePrizeWinnerMessage.bind(this))
    );
  }

  protected getInitialGame(gameIdentity: GameIdentity): Bingo75Game {
    const gameConfig = this.getGameConfiguration(gameIdentity);
    const maxNumberOfCallsPerGame = gameConfig.maxNumberOfCallsPerGame;
    const game = new Bingo75Game(gameIdentity, gameConfig);
    game.representation = {
      callboard: new GameCallboard(maxNumberOfCallsPerGame)
    };
    return game;
  }

  protected handleAuthorisationMessage(payload: IBingo75AuthenticationResponse): void {
    this.changeGameConnectionState('Authorised');
    this.patterns.currentPattern = payload.currentpattern;
    this.patterns.currentPatternName = payload.currentPatternName;
  }

  protected handleCatchUpMessage(payload: CatchUpData): void {
    this.game.representation.callboard.update(payload.callValues);
    this.changeGameRepresentation({
      gameId: payload.gameNo,
      numberOfPlayers: payload.numberOfPlayers,
      isLinkedGame: payload.isLinkedGame,
      prizeStructure: payload.prizeStructure,
    });

    this.patterns.nextPattern = payload.pattern;
    this.patterns.nextPatternName = payload.patternName;
  }

  protected handleNextGameMessage(payload: NextGameData): void {
    this.game.updateRepresentation({
      nextGame: {
        countDownToNextGameInSeconds: payload.time,
        isLinkedGame: payload.isLinkedGame,
        prizeStructure: payload.prizeStructure,
        patternName: payload.patternName
      }
    });

    if (this.patterns.nextPattern && this.patterns.nextPattern !== payload.pattern) {
      this.patterns.currentPattern = this.patterns.nextPattern;
      this.patterns.currentPatternName = this.patterns.nextPatternName;
      this.patterns.nextPattern = payload.pattern;
      this.patterns.nextPatternName = payload.patternName;
    }

    this.notifyNextGameRepresentationChanged();
  }

  protected handleResetMessage(payload: ResetData): void {
    this.game.representation.callboard.reset();
    this.changeGameState(GameState.InPlay);
    this.changeGameRepresentation({
      gameId: payload.gameId,
      numberOfPlayers: payload.numberOfPlayers,
      nextGame: null,
      isJackpotWon: null,
      prizeStructure: payload.prizeStructure,
      winners: null,
    });
  }

  protected changeGameRepresentation(newGameRepresentation: Partial<IBingo75GameRepresentation>): void {
    this.game.updateRepresentation(newGameRepresentation);
    this.notifyGameRepresentationChanged();
  }
}

export { Bingo75GameController, IBingo75GameRepresentation };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/bingo-75/_domain/controller/game-controller.ts